import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home, Search, Bot, Building2, Hammer, TrendingUp,
  Heart, Bell, Calculator, Scale, Users, Flame,
  ChevronDown, Star, LogIn, Menu, X, Sparkles
} from 'lucide-react';
import UserAuthMenu from '../UserAuthMenu';

const NAV_ITEMS = [
  { label: 'Főoldal',           icon: Home,        to: '/' },
  { label: 'AI Kereső',         icon: Bot,         to: '/ai-transform', badge: 'ÚJ' },
  { label: 'Ingatlanok',        icon: Building2,   to: '/elado-ingatlanok' },
  { label: 'Eladás / Kiadás',   icon: Hammer,      to: '/property-upload' },
  { label: 'Hot Deals',         icon: Flame,       to: '/hot-deals', badge: '🔥' },
  { label: 'Befektetések',      icon: TrendingUp,  to: '/befekteto' },
  { label: 'Kedvencek',         icon: Heart,       to: '/kedvencek' },
  { label: 'Értesítések',       icon: Bell,        to: '/ertesitesek', badge: '3' },
  { label: 'Kalkulátor',        icon: Calculator,  to: '/arkalkulator' },
  { label: 'Jogi háttér',       icon: Scale,       to: '/jogi-nyilatkozat' },
  { label: 'Ajánlói program',   icon: Users,       to: '/affiliateregister' },
  { label: 'Rólunk',            icon: Sparkles,    to: '/rolunk' },
];

const HOUSE_SUB = [
  { label: 'Egyedi házak',     to: '/egyedi-hazak' },
  { label: 'Mobilházak',       to: '/mobilhazak' },
  { label: 'Hopp ProConcept',  to: '/hopp-proconcept' },
  { label: 'Kész Házak',       to: '/kesz-hazak' },
];

export default function Sidebar({ collapsed, mobileOpen, onDesktopCollapse, onMobileClose }) {
  const location = useLocation();
  const [hazakOpen, setHazakOpen] = useState(false);

  const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + '/');

  /* Sidebar is visible on desktop always (width changes by collapsed state)
     On mobile it's an overlay drawer, visible only when mobileOpen=true */
  const desktopClass = collapsed ? 'lg:w-[64px]' : 'lg:w-[240px]';
  const mobileClass = mobileOpen ? 'translate-x-0' : '-translate-x-full';
  /* On desktop: always translate-x-0; on mobile: controlled by mobileOpen */

  return (
    <>
      {/* Mobile overlay backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={onMobileClose}
        />
      )}

      <aside
        className={`
          fixed top-0 left-0 h-full z-50 flex flex-col
          transition-all duration-300 ease-in-out
          bg-[#111111] border-r border-white/[0.06]
          w-[240px]
          ${mobileClass} lg:translate-x-0 ${desktopClass}
        `}
        style={{ backdropFilter: 'blur(20px)' }}
      >
        {/* Logo area */}
        <div className={`flex items-center gap-3 px-4 py-5 border-b border-white/[0.06] flex-shrink-0 ${collapsed ? 'lg:justify-center lg:px-2' : ''}`}>
          <Link to="/" className="flex items-center gap-3 min-w-0">
            <img
              src="/logo_ai.png"
              alt="HoppHomes"
              className="h-11 w-auto flex-shrink-0 object-contain"
            />
            {!collapsed && (
              <div className="text-[10px] text-[#6b7280] leading-tight whitespace-nowrap">
                SMART LIVING. REAL VALUE.
              </div>
            )}
          </Link>
          {/* Collapse toggle - desktop only */}
          <button
            onClick={() => onDesktopCollapse(!collapsed)}
            className="hidden lg:flex ml-auto flex-shrink-0 w-7 h-7 items-center justify-center rounded-lg hover:bg-white/[0.06] text-[#6b7280] hover:text-white transition-colors"
          >
            <Menu size={14} />
          </button>
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5 scrollbar-thin">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                title={collapsed ? item.label : undefined}
                className={`
                  group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium
                  transition-all duration-200 relative
                  ${active
                    ? 'bg-[#e31937] text-white shadow-lg shadow-red-900/30'
                    : 'text-[#9ca3af] hover:text-white hover:bg-white/[0.06]'
                  }
                  ${collapsed ? 'lg:justify-center lg:px-2' : ''}
                `}
              >
                <Icon size={16} className="flex-shrink-0" />
                {!collapsed && (
                  <span className="truncate flex-1">{item.label}</span>
                )}
                {!collapsed && item.badge && (
                  <span className={`
                    text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0
                    ${active ? 'bg-white/20 text-white' : 'bg-[#e31937] text-white'}
                  `}>
                    {item.badge}
                  </span>
                )}
                {collapsed && item.badge && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#e31937] text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                    {typeof item.badge === 'string' && item.badge.length <= 1 ? item.badge : '!'}
                  </span>
                )}
              </Link>
            );
          })}

          {/* Házak accordion */}
          <div>
            <button
              onClick={() => setHazakOpen(!hazakOpen)}
              className={`
                w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium
                transition-all duration-200
                text-[#9ca3af] hover:text-white hover:bg-white/[0.06]
                ${collapsed ? 'lg:justify-center lg:px-2' : ''}
              `}
            >
              <Building2 size={16} className="flex-shrink-0" />
              {!collapsed && (
                <>
                  <span className="flex-1 text-left truncate">Házak</span>
                  <ChevronDown
                    size={14}
                    className={`flex-shrink-0 transition-transform duration-200 ${hazakOpen ? 'rotate-180' : ''}`}
                  />
                </>
              )}
            </button>
            {!collapsed && hazakOpen && (
              <div className="ml-6 mt-1 space-y-0.5 animate-fade-in">
                {HOUSE_SUB.map((sub) => (
                  <Link
                    key={sub.to}
                    to={sub.to}
                    className={`
                      flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium
                      transition-all duration-200
                      ${isActive(sub.to)
                        ? 'text-[#e31937] bg-[#e31937]/10'
                        : 'text-[#6b7280] hover:text-white hover:bg-white/[0.04]'
                      }
                    `}
                  >
                    <span className="w-1 h-1 rounded-full bg-current flex-shrink-0" />
                    {sub.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </nav>

        {/* FOB PRIME promo block */}
        {!collapsed && (
          <div className="px-3 py-3 border-t border-white/[0.06] flex-shrink-0">
            <div className="bg-gradient-to-br from-[#e31937]/20 to-[#cc1530]/10 border border-[#e31937]/20 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-2">
                <Star size={12} className="text-[#e31937] flex-shrink-0" />
                <span className="text-white text-xs font-bold">HH PRIME</span>
              </div>
              <p className="text-[#9ca3af] text-[10px] leading-relaxed mb-2">
                Exkluzív ajánlatok, személyes tanácsadás
              </p>
              <Link
                to="/affiliateregister"
                className="block w-full text-center bg-[#e31937] hover:bg-[#cc1530] text-white text-xs font-bold py-1.5 rounded-lg transition-colors"
              >
                Csatlakozom
              </Link>
            </div>
          </div>
        )}

        {/* User profile bottom */}
        <div className={`px-3 py-3 border-t border-white/[0.06] flex-shrink-0 ${collapsed ? 'lg:px-2' : ''}`}>
          <UserAuthMenu compact={collapsed} />
        </div>
      </aside>
    </>
  );
}
